import axios from "axios";

/* ===========================
   BASE URL
=========================== */
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://www.twglarticles.in/api"
    : "http://localhost:5000/api");

/* ===========================
   DELAY HELPER
=========================== */


/* ===========================
   AXIOS INSTANCE
=========================== */
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // ✅ VERY IMPORTANT (cookies auto sent)
  timeout: 60000
});

/* ===========================
   REFRESH CONTROL
=========================== */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

/* ===========================
   RESPONSE INTERCEPTOR
=========================== */
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    /* ===========================
       HANDLE 401 (TOKEN EXPIRED)
    =========================== */
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/login")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: () => resolve(api(originalRequest)),
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // ✅ Backend handles cookies automatically
        await api.post("/twgoldlogin/refresh-token");

        processQueue(null);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        isRefreshing = false;

        // ✅ CALL LOGOUT API instead of clearing cookies manually
        try {
          await api.post("/twgoldlogin/logout");
        } catch (e) {}

        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

//     /* ===========================
//        RETRY LOGIC (2 TIMES)
//     =========================== */
//   // ❌ Skip retry for Aadhaar APIs
// if (
//   originalRequest.url.includes('/aadhaar/generate-otp') ||
//   originalRequest.url.includes('/aadhaar/verify-otp')
// ) {
//   return Promise.reject(error);
// }

// if (!originalRequest._retryCount) {
//   originalRequest._retryCount = 0;
// }

// if (originalRequest._retryCount < 2) {
//   originalRequest._retryCount += 1;

//   await delay(2000);

//   return api(originalRequest);
// }

return Promise.reject(error);  }
);

/* ===========================
   AUTH APIs
=========================== */
export const login = (data) =>
  api.post("/twgoldlogin/login", data);

export const logout = () =>
  api.post("/twgoldlogin/logout");

export const refreshToken = () =>
  api.post("/twgoldlogin/refresh-token");

/* ===========================
   PROFILE APIs
=========================== */
export const getProfile = () =>
  api.get("/twgoldlogin/profile");

export const updateProfile = (data) =>
  api.put("/twgoldlogin/profile", data);

export const changePassword = (data) =>
  api.put("/twgoldlogin/change-password", data);

// =========================================================
// AADHAAR VERIFICATION ENDPOINTS
// =========================================================

export const generateUserAadhaarOtp = (data) => api.post('/twgoldlogin/aadhaar/generate-otp', data);
export const verifyUserAadhaarOtp = (data) => api.post('/twgoldlogin/aadhaar/verify-otp', data);
export const createCustomerWithAadhaar = (data) => api.post('/twgoldlogin/customer/create-with-aadhaar', data);
export const createUserWithAadhaar = (data) => api.post('/twgoldlogin/user/create-with-aadhaar', data);
export const getVerifiedAadhaarDetails = (data) => api.post('/twgoldlogin/user/aadhaar/get-verified-details', data);
export const getAadhaarVerificationStatus = (aadhaarNumber) => api.get(`/twgoldlogin/user/aadhaar/status/${aadhaarNumber}`);
// =========================================================
// USER MANAGEMENT ENDPOINTS
// =========================================================

export const createUser = (data) => api.post('/twgoldlogin/user/create', data);
export const getAllAdmins = () => api.get('/twgoldlogin/admins');
export const getAllUsers = () => api.get('/twgoldlogin/users');
export const getUsersByRole = (role) => api.get(`/twgoldlogin/users/role/${role}`);
export const getUsersByBranch = (branch) => api.get(`/twgoldlogin/branch/${branch}/users`);
export const getUsersByDepartment = (department) => api.get(`/twgoldlogin/department/${department}/users`);
export const updateUserStatus = (id, data) => api.put(`/twgoldlogin/user/${id}/status`, data);
export const updateUserPermissions = (id, data) => api.put(`/twgoldlogin/user/${id}/permissions`, data);


// =========================================================
// BRANCH MANAGEMENT ENDPOINTS
// =========================================================

export const createBranch = (data) => api.post('/twgoldbranch/branches', data);
export const getAllBranches = () => api.get('/twgoldbranch/branches');
export const getBranchById = (id) => api.get(`/twgoldbranch/branches/${id}`);
export const updateBranch = (id, data) => api.put(`/twgoldbranch/branches/${id}`, data);
export const getBranchPerformance = () => api.get('/twgoldbranch/branches-performance');
export const addEmployeeToBranch = (branchId, employeeId) => api.post(`/twgoldbranch/branches/${branchId}/employees/${employeeId}`);

// Fetch branches (standardized response handler)
export const fetchBranches = async () => {
  const res = await api.get('/twgoldbranch/branches');
  return res.data?.success && Array.isArray(res.data?.data?.branches)
    ? res.data.data.branches
    : [];
};

// =========================================================
// ACTIVITY LOG ENDPOINTS
// =========================================================

export const getRecentActivities = () => api.get('/twgoldbranch/activities/recent');
export const getActivitiesByDateRange = (params) => api.get('/twgoldbranch/activities/date-range', { params });
export const getUserActivities = (userId) => api.get(`/twgoldbranch/activities/user/${userId}`);


// Get customer by Customer ID
export const getCustomerByCustomerId = (customerId) =>
  api.get(`/twgoldcustomer/customers/by-customer-id/${customerId}`);

// Update daily gold rates (ADMIN only)
export const updateDailyGoldRates = (data) =>
  api.post('/twgoldrate/gold-rates', data);

// Get current gold rates (AUTH required)
export const getCurrentGoldRates = () =>
  api.get('/twgoldrate/gold-rates/current');

// Get gold rate history
export const getGoldRateHistory = () =>
  api.get('/twgoldrate/gold-rates/history');

// Create gold loan (Clerk)
export const createGoldLoan = (data) =>
  api.post('/twgoldloans/loans', data);

// Loan preview / EMI calculation (Clerk)
export const calculateGoldLoan = (data) =>
  api.post('/twgoldloans/loans/calculate', data);

// Get loans created by logged-in employee
export const getMyLoans = () =>
  api.get('/twgoldloans/loans/my');

// Collect EMI (Clerk)
export const collectLoanEmi = (data) =>
  api.post('/twgoldloans/loans/collect-emi', data);

// Get loans pending approval (Manager)
export const getPendingLoans = () =>
  api.get('/twgoldloans/loans/pending-approval');

// Approve / Reject loan (Manager)
export const decideLoan = (loanId, data) =>
  api.post(`/twgoldloans/loans/${loanId}/decision`, data);

// =========================================================
// DASHBOARD & ROLE-BASED ACCESS ROUTES
// =========================================================

// Manager Dashboard
export const getManagerDashboard = () =>
  api.get('/twgoldlogin/manager/dashboard');

// Manager Analytics
export const getManagerMonthlyStats = () =>
  api.get('/twgoldlogin/manager/monthly-stats');

export const getManagerSLABuckets = () =>
  api.get('/twgoldlogin/manager/sla-buckets');

export const fetchInventorySummary = () =>
  api.get('/twgoldlogin/manager/fetch-inventory');

// Employee Dashboard
export const getEmployeeDashboard = () =>
  api.get('/twgoldlogin/employee/dashboard');

// Grivirence Dashboard
export const getGrivirenceDashboard = () =>
  api.get('/twgoldlogin/grivirence/dashboard');

// Admin / Management
export const getManagementData = () =>
  api.get('/twgoldlogin/management/data');

export const getAllLoans = () => {
  return api.get('/twgoldloans/loans/all'); // ✅ matches backend route
};

export const getBranchComparison = () =>
  api.get('/twgoldlogin/admin/branch-comparison');

// 🔍 Search Employees (for UI)
export const searchEmployees = (params) =>
  api.get('/twgoldbranch/employees/search', { params });

// =========================================================
// 🏦 BRANCH – EMPLOYEE MANAGEMENT (MISSING APIs)
// =========================================================

// ❌ Remove employee from branch
export const removeEmployeeFromBranch = (branchId, employeeId) =>
  api.delete(`/twgoldbranch/branches/${branchId}/employees/${employeeId}`);

// 🔁 Transfer employee to another branch
// payload example: { toBranchId: '...' }
export const transferEmployee = (employeeId, data) =>
  api.post(`/twgoldbranch/employees/${employeeId}/transfer`, data);

// 🧾 Employee branch history (audit / timeline)
export const getEmployeeBranchHistory = (employeeId) =>
  api.get(`/twgoldbranch/employees/${employeeId}/branch-history`);

// =========================================================
// 👥 BULK EMPLOYEE ASSIGNMENT (OPTIONAL BUT RECOMMENDED)
// =========================================================

// Bulk assign employees to branch
// payload example: { employees: ['id1','id2'] }
export const addEmployeesToBranchBulk = (branchId, data) =>
  api.post(`/twgoldbranch/branches/${branchId}/employees/bulk`, data);

// =========================================================
// 📊 BRANCH DASHBOARD STATS
// =========================================================

export const getBranchDashboardStats = () =>
  api.get('/twgoldbranch/dashboard/stats');


export default api ;